import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { verifyToken as verifyTokenAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const tokenRefreshTimer = useRef(null);

  const refreshToken = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success && data.token) {
        localStorage.setItem('token', data.token);
        console.log('Token refreshed successfully');
        return true;
      } else {
        console.error('Token refresh failed:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }, []);

  const startTokenRefreshTimer = useCallback(() => {
    if (tokenRefreshTimer.current) {
      clearInterval(tokenRefreshTimer.current);
    }
    tokenRefreshTimer.current = setInterval(async () => {
      try {
        await refreshToken();
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }, 50 * 60 * 1000);
  }, [refreshToken]);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      console.log('Initializing auth, token exists:', !!token);
      
      if (token) {
        try {
          const response = await verifyTokenAPI();
          console.log('Token verification response:', response);
          
          if (response.valid) {
            setUser(response.user);
            if (response.user.allOrganisations) {
              setOrganisations(response.user.allOrganisations);
            }
            if (response.user.organisation) {
              setSelectedOrganisation(response.user.organisation);
            }
            // Start token refresh timer
            startTokenRefreshTimer();
          } else {
            console.log('Token invalid, removing');
            localStorage.removeItem('token');
            localStorage.removeItem('lastRoute');
            localStorage.removeItem('redirectAfterLogin');
          }
        } catch (error) {
          console.error('Auth verification failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('lastRoute');
          localStorage.removeItem('redirectAfterLogin');
        }
      } else {
        console.log('No token found');
        // Clear any saved routes when not authenticated
        localStorage.removeItem('lastRoute');
        localStorage.removeItem('redirectAfterLogin');
      }
      setLoading(false);
    };

    initAuth();

    return () => {
      if (tokenRefreshTimer.current) {
        clearInterval(tokenRefreshTimer.current);
      }
    };
  }, [startTokenRefreshTimer]);

  const login = (token, userData) => {
    console.log('Login, setting token and user data');
    localStorage.setItem('token', token);
    setUser(userData);
    if (userData.allOrganisations) {
      setOrganisations(userData.allOrganisations);
    }
    if (userData.organisation) {
      setSelectedOrganisation(userData.organisation);
    }
    startTokenRefreshTimer();
  };

  const logout = async () => {
    // Fire logout log — best effort, don't block UI
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (e) {
      console.warn('Logout log failed:', e.message);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('lastRoute');
    localStorage.removeItem('redirectAfterLogin');
    if (tokenRefreshTimer.current) {
      clearInterval(tokenRefreshTimer.current);
      tokenRefreshTimer.current = null;
    }
    setUser(null);
    setOrganisations([]);
    setSelectedOrganisation(null);
  };

  const switchOrganisation = (organisation) => {
    setSelectedOrganisation(organisation);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    organisations,
    selectedOrganisation,
    setSelectedOrganisation: switchOrganisation,
    isAuthenticated: !!user,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};